import {
  AccountsService,
  GetPricingTableResponse,
  PricingTable,
  PricingTableKey,
} from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { Button } from 'base/ui/button';
import { RedErrorText } from 'base/ui/form';
import { IconNewTab } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { getCurrentPlanKey, PricingPage } from 'blocks/billing/PricingPage';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import {
  AcceptPlanDialogUrlState,
  AcceptPlanOfferDialog,
} from './accept_plan_offer';
import { CancelPlanDialog } from './cancel_plan';
import styles from './plans.css';

export const ChoosePlan = observer(function _ChoosePlan({
  mustChoosePlan,
  accountsService,
  accountStore,
  dialogState,
  setDialogState,
  onSelectPlan,
  onDone,
}: {
  mustChoosePlan: boolean;
  accountsService: AccountsService;
  accountStore: AccountStore;
  dialogState: AcceptPlanDialogUrlState | undefined;
  onSelectPlan: (planKey: PricingTableKey) => void;
  setDialogState: (state: AcceptPlanDialogUrlState | undefined) => void;
  onDone: (
    planChosen: keyof PricingTable | undefined,
    hasCoupon: boolean,
    urlState?: AcceptPlanDialogUrlState,
  ) => void;
}) {
  const [res, setRes] = React.useState<GetPricingTableResponse | undefined>();
  const [error, setError] = React.useState<Error | undefined>();
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);

  const canTogglePeriod =
    res != null &&
    ((dialogState?.planKey?.startsWith('pro') &&
      res.table.proMonthly?.status === 'available') ||
      (dialogState?.planKey?.startsWith('starter') &&
        res.table.starterMonthly?.status === 'available') ||
      (dialogState?.planKey?.startsWith('team') &&
        res.table.teamMonthly?.status === 'available'));

  const handleCancel = useCallback(() => {
    accountsService.getChurnkeyHash().then(
      ({ hash, customerId, subscriptionId }) => {
        window.churnkey.init('show', {
          subscriptionId,
          customerId,
          authHash: hash,
          appId: 'mdnpklk56',
          mode: 'live',
          provider: 'stripe',
        });
      },
      (error) => {
        console.error(
          'Error fetching churnkey hash - falling back to old method',
          error,
        );
        setCancelDialogOpen(true);
      },
    );
  }, [accountsService]);

  React.useEffect(() => {
    accountsService
      .getPricingTable()
      .then((res) => setRes(res))
      .catch(setError);
  }, [accountsService]);

  // A quirk of the system is that customers are always at least on the free
  // plan, however we need to distinguish this in order to switch the experience
  const currentPlanKey =
    !mustChoosePlan && res ? getCurrentPlanKey(res.table) : undefined;

  const getOnClick = useCallback(
    (planKey: PricingTableKey) => () => {
      // Free plan is an edge case…
      if (planKey === 'free') {
        return currentPlanKey == null
          ? // …either it's continuing without paying (signup page)
            onDone('free', false)
          : // …or they're cancelling (upgrade page)
            handleCancel();
      }

      setDialogState({ planKey });
      return onSelectPlan(planKey);
    },
    [setDialogState, onSelectPlan, currentPlanKey, onDone, handleCancel],
  );

  return (
    <div className={styles.container}>
      <Rows spacing={32} xalign="center">
        {dialogState && (
          <AcceptPlanOfferDialog
            urlState={dialogState}
            setUrlState={setDialogState}
            accountsService={accountsService}
            accountStore={accountStore}
            onClose={() => setDialogState(undefined)}
            redirectOnSuccess={onDone}
            canChangePeriod={canTogglePeriod}
          />
        )}
        {cancelDialogOpen && (
          <CancelPlanDialog
            accountsService={accountsService}
            onClose={() => setCancelDialogOpen(false)}
            onDone={() => onDone('free', false)}
            onUpsell={(state) => {
              setCancelDialogOpen(false);
              setDialogState(state);
            }}
          />
        )}
        {!mustChoosePlan && (
          <div className={styles.cancelButton}>
            <Button
              variant="tertiary"
              background="grey2"
              label="Cancel"
              onClick={() => onDone(undefined, false)}
              size="large"
            />
          </div>
        )}
        <RedErrorText error={error} />
        <PricingPage
          accountStore={accountStore}
          table={res?.table}
          getOnClick={getOnClick}
          currentPlanKey={currentPlanKey}
        />
        <Button
          variant="subtle"
          size="large"
          label="Compare all features"
          href="https://www.relume.io/pricing#compare"
          iconRight={IconNewTab}
          target="_blank"
        />
      </Rows>
    </div>
  );
});
